import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'dig-components/buttons';
import { Text } from 'dig-components/typography';
import { ChoiceChip } from 'dig-components/chips';
import styles from './api-setting.scss';
import { trackHeapCustomEvent } from 'common/utils/heap';

type APIAppSectionProps = {
  createdApps: ApiAppProps[],
  grantedApps: ApiAppProps[],
}

type ThirdPartyAppsProps = {
  apps: ApiAppProps[],
}

export type ApiAppProps = {
  isApproved: boolean,
  shortName: string,
  clientId: string,
  mainDomainName: string,
  scopeNames: string[]
}

// These classes are temporarily used to connect to JS functions rendered by PHP.
const helperClasses = {
  details: 'm-settings--api--app--view-btn',
  edit: 'm-settings--api--app--edit-btn',
  delete: 'm-settings--api--app--delete-btn',
  revoke: 'm-settings--api--app--revoke-btn',
  row: `m-settings--api--apps--row ${styles.apiApp}`,
};

function ApiApp({
  isApproved,
  shortName,
  clientId,
}: ApiAppProps) {
  const preventDefaultAction = React.useCallback((
    fn?: (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void,
  ) => (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    evt.preventDefault();

    if (fn) fn(evt);
  }, []);

  // Attributes is temporarily used to connect to JS functions rendered by PHP.
  const customAttribs = {
    client_id: `deleteAppButton_${clientId}`,
  };

  return (
    <section className={helperClasses.row} {...customAttribs}>
      <ul>
        <li>
          <Text isBold>{shortName}</Text>
        </li>
        <li>
          <Text>
            <FormattedMessage
              id=''
              defaultMessage='Client ID: {clientId}'
              description='label, settings page, a special important string of various characters used to secure connection to remote services'
              values={{
                clientId,
              }} />
          </Text>
        </li>
        <li>
          <ChoiceChip
            data-qa-ref="account-app-choice-chip-data"
            size='small'
            onClick={preventDefaultAction()}
            className={styles.status}
            {...isApproved ? { isSuccess: true } : { isAlert: true }}
          >
            {isApproved ? (
              <FormattedMessage
                id=''
                defaultMessage='Approved'
                description='officially agree to or accept as satisfactory.' />
            ) : (
              <FormattedMessage
                id=''
                defaultMessage='Not Approved'
                description='not officially agree to or not accept as satisfactory.' />
            )}
          </ChoiceChip>
        </li>
      </ul>
      <div className={styles.actions}>
        {/* span is needed so that the click handler for showing details (from PHP) is attached */}
        <span className={helperClasses.details}>
          <Button
            onClick={preventDefaultAction(() => trackHeapCustomEvent('account_app_detail_button_click'))}
            rel={clientId}
            variant='opacity'
            data-qa-ref="account-app-detail-button"
          >
            <FormattedMessage
              id=''
              defaultMessage='Details'
              description='button, settings page, button to see the extra information about the application connected to a users account' />
          </Button>
        </span>
        <span className={helperClasses.edit}>
          <Button
            onClick={preventDefaultAction(() => trackHeapCustomEvent('account_app_edit_button_click'))}
            rel={clientId}
            variant='opacity'
            data-qa-ref="account-app-edit-button"
          >
            <FormattedMessage
              id=''
              defaultMessage='Edit'
              description='button, settings page, button that change settings on an application connected to a users account' />
          </Button>
        </span>
        <span className={helperClasses.delete}>
          <Button
            onClick={preventDefaultAction(() => trackHeapCustomEvent('api_key_delete_button_click'))}
            rel={customAttribs.client_id}
            variant='opacity'
            data-qa-ref="account-app-delete-button"
            id="account-app-delete-button"
          >
            <FormattedMessage
              id=''
              defaultMessage='Delete'
              description='button, settings page, will remove applications that are attached to their account' />
          </Button>
        </span>
      </div>
    </section>
  );
}

function ThirdPartyApp({
  shortName,
  mainDomainName,
  scopeNames,
  clientId,
}: ApiAppProps) {
  const preventDefaultAction = (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    evt.preventDefault();
  };
  const intl = useIntl();

  // Attribute is temporarily used to connect to JS functions rendered by PHP.
  const customAttribs = {
    client_id: `deleteAppButton_${clientId}`,
  };

  return (
    <section className={helperClasses.row} {...customAttribs}>
      <ul>
        <li><Text isBold>{shortName}</Text></li>
        <li>
          <Text data-qa-ref="account-app-third-party-domain-data">
            <FormattedMessage
              id=''
              defaultMessage='Domain: {domainName}'
              description='label, domain name'
              values={{
                domainName: <span className={styles.domain}>{mainDomainName}</span>,
              }} />
          </Text>
        </li>
        <li>
          <Text data-qa-ref="account-app-third-party-scope-button">
            <FormattedMessage
              id=''
              defaultMessage='Scopes: {preformattedList}'
              description='Label, describes app resources third party is allowed to access'
              values={{
                preformattedList: intl.formatList(
                  scopeNames, {
                    type: 'conjunction',
                    style: 'narrow',
                  },
                ),
              }} />
          </Text>
        </li>
      </ul>
      <div className={styles.actions}>
        {/* span is needed so that the click handler for showing details (from PHP) is attached */}
        <span className={helperClasses.revoke}>
          <Button
            onClick={preventDefaultAction}
            rel={customAttribs.client_id}
            variant='opacity'
            data-qa-ref="account-app-third-party-remove-button"
          >
            <FormattedMessage
              id=''
              defaultMessage='Remove'
              description='button, settings page, button to remove applications  the user had previous activated' />
          </Button>
        </span>
      </div>
    </section>
  );
}

function ThirdPartyApps({ apps }: ThirdPartyAppsProps) {
  return (
    <div className='m-settings--section is-api no-margin'>
      <section>
        <ul>
          <li>
            <Text size='large' isBold>
              <FormattedMessage
                id=''
                description='settings page title for a list of third party apps that user has granted access to their account'
                defaultMessage='Third-party applications' />
            </Text>
          </li>
          <li>
            <Text>
              <FormattedMessage
                id=''
                defaultMessage='Apps that you have authorized to perform actions on HelloSign on your behalf'
                description='text describing what Third-party applications section is for' />
            </Text>
          </li>
        </ul>
      </section>
      {
        apps.map((app: ApiAppProps) => {
          return <ThirdPartyApp key={app.clientId} {...app} />;
        })
      }
    </div>
  );
}

function APIAppSection({
  createdApps,
  grantedApps,
}: APIAppSectionProps) {
  return (
    <>
      {Array.isArray(grantedApps) && grantedApps.length > 0 && (
        <ThirdPartyApps apps={grantedApps} />
      )}
      <div className='m-settings--section is-api no-margin'>
        <section>
          <ul>
            <li>
              <Text size='large' isBold>
                <FormattedMessage
                  id=''
                  description='title, settings page, about a list of app that  the user has attached to their account'
                  defaultMessage='API apps' />
              </Text>
            </li>
            <li>
              <Text>
                <FormattedMessage
                  id=''
                  defaultMessage='Manage the apps integrated with your API'
                  description='description text describing what the App setting section is for' />
              </Text>
            </li>
          </ul>
          <Button
            variant='outline'
            href='/oauth/createAppForm'
            data-qa-ref="account-app-section-create-button"
            onClick={() => trackHeapCustomEvent('api_app_create_button')}
          >
            <FormattedMessage
              id=''
              defaultMessage='Create app'
              description='button, settings page, will activate a feature that will allow the user to add custom applications on their account' />
          </Button>
        </section>
        {
          createdApps.map((app: ApiAppProps) => {
            return <ApiApp key={app.clientId} {...app} />;
          })
        }
      </div>
    </>
  );
}

export default APIAppSection;
